// src/components/layout.tsx

import React, { useEffect } from 'react'
import { useAppSelector } from '../../store/hooks'
import { selectIsAuthenticated } from '../../store/slices/authSlice'
import {
  selectSelectedProjectId,
  selectActiveDatasetSubView,
  selectActiveInferenceSubView,
} from '../../store/slices/projectSlice'
import { useGetProjectQuery } from '../../services/projectsApi'
import AvNavbar from '../AvNavBar'
import { useLocation, useNavigate } from 'react-router-dom'
import CollapsibleNav from './Sidebar/CollapsibleNav'
import { HeaderRow } from './HeaderRow'
import {
  RootContainer,
  LayoutContainer,
  NavbarContainer,
  MainContentContainer,
  HeaderContainer,
} from './Layout.styles'
import styled from 'styled-components'
import ExportProgressToast from '../ExportProgressToast'

const AdjustedMainContentContainer = styled(MainContentContainer)`
  // Footer is commented out, so no padding needed
`

// Page header text lookup
const PAGE_HEADER_TEXT = {
  datasets: {
    Overview: {
      title: 'Dataset Overview',
      subtitle: 'Summarize key statistics, class distributions, and data quality insights.',
    },
    Annotations: {
      title: 'Annotations',
      subtitle: 'Label your datasets here',
    },
    Tag: {
      title: 'Tag',
      subtitle: 'Create, delete and manage classification tags',
    },
    Content: {
      title: 'Content',
      subtitle: 'Manage your dataset content and images',
    },
    Export: {
      title: 'Export',
      subtitle: 'Export your dataset in various formats',
    },
    'Dataset Selector': {
      title: 'Dataset Selector',
      subtitle: 'Select and manage your dataset sources',
    },
  },
  models: {
    default: {
      title: 'Model View',
      subtitle: 'View and manage your models',
    },
    Overview: {
      title: 'Model Overview',
      subtitle: 'View model details and performance metrics',
    },
    Training: {
      title: 'Model Training',
      subtitle: 'Configure and monitor model training',
    },
    Performance: {
      title: 'Model Performance',
      subtitle: 'Analyze model metrics and validation results',
    },
    Deployment: {
      title: 'Model Deployment',
      subtitle: 'Deploy and manage model endpoints',
    },
  },
  inference: {
    'Adaptive Active Labeling': {
      title: '',
      subtitle: '',
    },
    'Real Time': {
      title: 'Real Time Inference',
      subtitle: 'Run real-time inference on your models',
    },
    'Custom Inference': {
      title: 'Custom Inference',
      subtitle: 'Configure and run custom inference jobs',
    },
    'Map (Beta)': {
      title: 'Map View',
      subtitle: 'Visualize geospatial data and predictions',
    },
    'Synthetic Data': {
      title: 'Synthetic Data Generation',
      subtitle: 'Enhance your dataset with AI-powered synthetic data generation tools',
    },
  },
}

// List of routes that don't require project selection
const noProjectRoutes = [
  '/login',
  '/projects',
  '/error-test',
  '/termsconditions',
  '/privacypolicy',
  '/docs',
  '/dashboard',
  '/profile',
]

interface LayoutProps {
  children: React.ReactNode
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const isAuthenticated = useAppSelector(selectIsAuthenticated)
  const location = useLocation()
  const selectedProjectId = useAppSelector(selectSelectedProjectId)
  const { data: project } = useGetProjectQuery(selectedProjectId ?? '', {
    skip: !selectedProjectId,
  })
  const navigate = useNavigate()
  const activeDatasetSubView = useAppSelector(selectActiveDatasetSubView)
  const activeInferenceSubView = useAppSelector(selectActiveInferenceSubView)

  React.useEffect(() => {
    const currentPath = location.pathname
    const requiresProject = !noProjectRoutes.some((route) => currentPath.startsWith(route))

    if (requiresProject && !selectedProjectId && !localStorage.getItem('selectedProjectId')) {
      navigate('/projects')
    }
  }, [selectedProjectId, location.pathname, navigate])

  useEffect(() => {
    const isNewProjectSelection =
      selectedProjectId && selectedProjectId !== localStorage.getItem('lastSelectedProjectId')

    if (isNewProjectSelection) {
      if (selectedProjectId) {
        localStorage.setItem('lastSelectedProjectId', selectedProjectId)
      }
    }
  }, [location.pathname, selectedProjectId])

  const getPageHeaderText = (path: string) => {
    switch (path) {
      case 'datasets':
        return PAGE_HEADER_TEXT.datasets[
          activeDatasetSubView as keyof typeof PAGE_HEADER_TEXT.datasets
        ]
      case 'models':
        return PAGE_HEADER_TEXT.models.default
      case 'inference':
        return PAGE_HEADER_TEXT.inference[
          activeInferenceSubView as keyof typeof PAGE_HEADER_TEXT.inference
        ]
      default:
        return {
          title: '',
          subtitle: '',
        }
    }
  }

  // const renderFooter = () => (
  //   <FooterContainer $visible={footerVisible}>
  //     <BackgroundUploadProgress />
  //     <ModelTrainingStatus />
  //   </FooterContainer>
  // )

  return (
    <RootContainer>
      {isAuthenticated && !location.pathname.startsWith('/docs') ? (
        <LayoutContainer>
          {/* Top Navbar */}
          <NavbarContainer>
            <AvNavbar />
          </NavbarContainer>

          {/* Header Row */}
          {(location.pathname.split('/')[1] === 'datasets' ||
            location.pathname.split('/')[1] === 'models' ||
            location.pathname.split('/')[1] === 'inference' ||
            location.pathname.split('/')[1] === 'overview') && (
            <HeaderContainer>
              <HeaderRow
                project={project}
                pageHeaderText={getPageHeaderText(location.pathname.split('/')[1])}
              />
            </HeaderContainer>
          )}

          {/* Main Content */}
          <AdjustedMainContentContainer>
            {(location.pathname.split('/')[1] === 'datasets' ||
              location.pathname.split('/')[1] === 'models' ||
              location.pathname.split('/')[1] === 'inference' ||
              location.pathname.split('/')[1] === 'dashboard') && <CollapsibleNav />}
            {children}
            <ExportProgressToast />
          </AdjustedMainContentContainer>

          {/* {renderFooter()} */}
        </LayoutContainer>
      ) : (
        <div style={{ width: '100%', height: '100%', overflow: 'hidden' }}>
          {children}
          <ExportProgressToast />
          {/* {renderFooter()} */}
        </div>
      )}
    </RootContainer>
  )
}

export default Layout
